/**
 * @file
 * @fileOverview Enhanced MixedReality class for handling WebXR and WebAR features.
 * @version 2.0.0
 * @author Parker Eli Bidigare (Enhanced by Claude)
 * @date 2025-03-21
 */

/**
 * MixedReality class for WebXR integration with A-Frame
 * @class
 * @classdesc A comprehensive class for managing WebXR sessions, including AR and VR modes.
 * Provides feature detection, hit testing, anchor management, plane detection, and more.
 */
class MixedReality {
  /**
   * Creates a new MixedReality instance.
   * @constructor
   * @param {Object} options - Configuration options
   * @param {Object} [options.handDetection=null] - Optional HandDetection instance for gesture recognition
   * @param {Function} [options.frameCallback=null] - Optional callback to run on each XR frame
   * @param {boolean} [options.debugMode=false] - Enable debug logging
   * @param {number} [options.lightUpdateInterval=10] - Frames to skip between light estimation updates (performance optimization)
   */
  constructor(options = {}) {
    // Extract options with defaults
    const {
      handDetection = null,
      frameCallback = null,
      debugMode = false,
      lightUpdateInterval = 10
    } = typeof options === 'object' ? options : {};

    this.handDetection = handDetection;
    this.frameCallback = frameCallback;
    this.debugMode = debugMode;
    this.lightUpdateInterval = lightUpdateInterval;
    
    // XR state
    this.xrSession = null;
    this.xrFrame = null;
    this.xrRefSpace = null;
    this.viewerSpace = null;
    this.isAR = false;
    this.isVR = false;
    this.currentMode = null;
    this.frameCount = 0;
    
    // A-Frame references
    this.scene = null;
    this.camera = null;
    
    // Feature tracking
    this.supportedFeatures = new Set();
    this.featureStatus = {};
    
    // AR feature instances
    this.planes = new Map();
    this.lightProbe = null;
    this.lightEstimate = null;
    this.depthSensing = null;
    this.localFloor = null;
    this.hitTestSource = null;
    this.domOverlayElement = null;
    this.imageTrackingResults = new Map();
    this.trackedImages = new Map();
    this.anchors = new Map();
    this.meshes = new Map();
    
    // Default settings
    this.defaultLightIntensity = 1.0;
    this.defaultEnvironmentIntensity = 1.0;
    
    // Hit test state
    this.lastHitTestPose = null;
    this.hitTestListeners = [];
    
    // Performance monitoring
    this.performanceMetrics = {
      avgFrameTime: 0,
      frameCount: 0,
      lastFrameTimestamp: 0
    };
    
    // Initialize A-Frame integration
    this.initAframe();
    
    // Bind methods to preserve 'this' context
    this.onXRFrame = this.onXRFrame.bind(this);
    this.onHitTestSourceLost = this.onHitTestSourceLost.bind(this);
    this.onImageTrackingResults = this.onImageTrackingResults.bind(this);
    this.onAnchorUpdate = this.onAnchorUpdate.bind(this);
  }

  /**
   * Initialize A-Frame integration
   * @private
   */
  initAframe() {
    this.log('Initializing A-Frame integration');
    // Find the A-Frame scene and camera
    this.scene = document.querySelector('a-scene');
    if (!this.scene) {
      console.warn('A-Frame scene not found. Make sure you have included A-Frame and created an a-scene element.');
    }
    this.camera = this.scene ? this.scene.querySelector('a-camera') : null;
    if (!this.camera) {
      console.warn('A-Frame camera not found. Make sure you have included a camera in your A-Frame scene.');
    }
    
    // Listen for A-Frame scene loaded event
    if (this.scene && this.scene.hasLoaded) {
      this.onSceneLoaded();
    } else if (this.scene) {
      this.scene.addEventListener('loaded', this.onSceneLoaded.bind(this));
    }
  }
  
  /**
   * Handler for A-Frame scene loaded event
   * @private
   */
  onSceneLoaded() {
    this.log('A-Frame scene loaded');
    // Register A-Frame event listeners
    this.scene.addEventListener('enter-vr', () => {
      this.log('Entered VR mode via A-Frame');
    });
    
    this.scene.addEventListener('exit-vr', () => {
      this.log('Exited VR mode via A-Frame');
    });
  }

  /**
   * Log a message if debug mode is enabled
   * @param {...any} args - Arguments to log
   */
  log(...args) {
    if (this.debugMode) {
      console.log('[MixedReality]', ...args);
    }
  }

  /**
   * Log a warning message
   * @param {...any} args - Arguments to log
   */
  warn(...args) {
    console.warn('[MixedReality]', ...args);
  }

  /**
   * Log an error message
   * @param {...any} args - Arguments to log
   */
  error(...args) {
    console.error('[MixedReality]', ...args);
  }

  /**
   * Check if a specific WebXR session mode is supported
   * @param {string} mode - The session mode to check ('immersive-ar', 'immersive-vr', etc.)
   * @returns {Promise<boolean>} - Promise resolving to whether the mode is supported
   */
  async isSessionModeSupported(mode) {
    try {
      if (!navigator.xr) {
        this.warn('WebXR not supported in this browser');
        return false;
      }
      
      return await navigator.xr.isSessionSupported(mode);
    } catch (err) {
      this.error('Error checking session support:', err);
      return false;
    }
  }

  /**
   * Checks which WebXR features are supported in a specific mode
   * @param {string} mode - The session mode to check features for
   * @returns {Promise<Set>} - A set of supported feature names
   */
  async checkFeatureSupport(mode) {
    if (!navigator.xr) {
      this.error('WebXR not supported');
      return new Set();
    }
  
    const features = [
      'local-floor',
      'hit-test',
      'dom-overlay',
      'light-estimation',
      'anchors',
      'depth-sensing',
      'plane-detection',
      'mesh-detection',
      'image-tracking'
    ];
  
    this.log(`Checking feature support for ${mode} mode...`);
    
    this.supportedFeatures.clear();
    this.featureStatus = {};
    
    for (const feature of features) {
      try {
        // First check if the feature is supported in this browser/device
        let supported = false;
        
        try {
          // Try the standard method first
          supported = await navigator.xr.isSessionSupported(mode, { requiredFeatures: [feature] });
        } catch (e) {
          // Some browsers implement features but don't properly report them via isSessionSupported
          // Fall back to checking if the browser vendors have experimental implementations
          const vendorPrefixed = [
            feature,
            `webxr-${feature}`,
            `experimental-${feature}`,
            `webkit-${feature}`,
            `mozilla-${feature}`,
            `chrome-${feature}`
          ];
          
          for (const prefixed of vendorPrefixed) {
            try {
              supported = await navigator.xr.isSessionSupported(mode, { requiredFeatures: [prefixed] });
              if (supported) {
                this.log(`Found support for ${feature} via ${prefixed}`);
                break;
              }
            } catch (innerErr) {
              // Ignore errors from vendor prefixes
            }
          }
        }
        
        if (supported) {
          this.supportedFeatures.add(feature);
          this.featureStatus[feature] = 'supported';
          this.log(`Feature '${feature}' is supported in ${mode} mode.`);
        } else {
          this.featureStatus[feature] = 'unsupported';
          this.log(`Feature '${feature}' is not supported in ${mode} mode.`);
        }
      } catch (error) {
        this.featureStatus[feature] = 'error';
        this.warn(`Error checking support for feature '${feature}' in ${mode} mode:`, error);
      }
    }
    
    return this.supportedFeatures;
  }

  /**
   * Sets a DOM element to use as overlay in AR mode
   * @param {HTMLElement|string} element - DOM element or ID of element to use as overlay
   * @returns {boolean} - Whether the element was successfully set
   */
  setDOMOverlay(element) {
    let domElement;
    
    if (typeof element === 'string') {
      domElement = document.getElementById(element);
      if (!domElement) {
        this.error(`Element with id "${element}" not found.`);
        return false;
      }
    } else if (element instanceof HTMLElement) {
      domElement = element;
    } else {
      this.error(`Invalid DOM overlay element: ${element}`);
      return false;
    }
    
    this.domOverlayElement = domElement;
    this.log(`DOM overlay set to element:`, domElement);
    return true;
  }

  /**
   * Set up local floor reference space
   * @private
   * @returns {Promise<boolean>} - Whether setup was successful
   */
  async setupLocalFloor() {
    if (!this.supportedFeatures.has('local-floor')) {
      this.log('Local-floor is not supported on this device.');
      return false;
    }
  
    this.log('Setting up local-floor');
  
    try {
      this.localFloor = await this.xrSession.requestReferenceSpace('local-floor');
      this.log('Local-floor reference space acquired');
      return true;
    } catch (error) {
      this.error('Error requesting local-floor reference space:', error);
      return false;
    }
  }

  /**
   * Set up hit test source for AR mode
   * @private
   * @returns {Promise<boolean>} - Whether setup was successful
   */
  async setupHitTest() {
    if (!this.supportedFeatures.has('hit-test')) {
      this.log('Hit-test is not supported on this device.');
      return false;
    }
  
    this.log('Setting up hit-test');
  
    try {
      // Request viewer reference space for hit testing
      this.viewerSpace = await this.xrSession.requestReferenceSpace('viewer');
      
      // Create hit test source
      this.hitTestSource = await this.xrSession.requestHitTestSource({
        space: this.viewerSpace,
        offsetRay: new XRRay() // Default is center of viewport
      });
      
      // Listen for hit test source lost event
      this.xrSession.addEventListener('hitTestSourceLost', this.onHitTestSourceLost);
      
      this.log('Hit test source created');
      return true;
    } catch (error) {
      this.error('Error setting up hit test:', error);
      return false;
    }
  }

  /**
   * Handle hit test source lost event
   * @private
   * @param {Event} event - The hit test source lost event
   */
  onHitTestSourceLost(event) {
    this.log('Hit test source lost:', event);
    this.hitTestSource = null;
    
    // Attempt to recreate hit test source
    this.setupHitTest().catch(err => {
      this.error('Failed to recreate hit test source:', err);
    });
  }
  
  /**
   * Add a hit test listener
   * @param {Function} callback - Function to call when hit test results change
   * @returns {number} - ID of the listener for removal
   */
  addHitTestListener(callback) {
    if (typeof callback !== 'function') {
      this.error('Hit test listener must be a function');
      return -1;
    }
    
    const id = Date.now();
    this.hitTestListeners.push({ id, callback });
    return id;
  }
  
  /**
   * Remove a hit test listener
   * @param {number} id - ID of the listener to remove
   * @returns {boolean} - Whether the listener was successfully removed
   */
  removeHitTestListener(id) {
    const initialLength = this.hitTestListeners.length;
    this.hitTestListeners = this.hitTestListeners.filter(listener => listener.id !== id);
    return this.hitTestListeners.length < initialLength;
  }

  /**
   * Perform hit test on the current frame
   * @private
   * @param {XRFrame} frame - The current XR frame
   * @returns {XRPose|null} - The hit test pose or null if no hit
   */
  performHitTest(frame) {
    if (!this.hitTestSource || !frame) return null;
  
    try {
      const hitTestResults = frame.getHitTestResults(this.hitTestSource);
      if (hitTestResults.length > 0) {
        const referenceSpace = this.localFloor || this.xrRefSpace;
        if (!referenceSpace) return null;
        
        const hitPose = hitTestResults[0].getPose(referenceSpace);
        if (hitPose) {
          // Store the last hit test pose
          this.lastHitTestPose = hitPose;
          
          // Notify listeners
          this.hitTestListeners.forEach(listener => {
            try {
              listener.callback(hitPose, hitTestResults[0]);
            } catch (err) {
              this.error('Error in hit test listener:', err);
            }
          });
          
          return hitPose;
        }
      }
      return null;
    } catch (error) {
      this.error('Error performing hit test:', error);
      return null;
    }
  }

  /**
   * Set up light estimation
   * @private
   * @returns {Promise<boolean>} - Whether setup was successful
   */
  async setupLightEstimation() {
    if (!this.supportedFeatures.has('light-estimation')) {
      this.log('Light estimation is not supported on this device.');
      return false;
    }
  
    this.log('Setting up light-estimation');
  
    try {
      this.lightProbe = await this.xrSession.requestLightProbe();
      this.log('Light probe acquired');
      return true;
    } catch (error) {
      this.error('Error requesting light probe:', error);
      return false;
    }
  }

  /**
   * Update light estimation based on the current frame
   * @private
   * @param {XRFrame} frame - The current XR frame
   */
  updateLightEstimation(frame) {
    if (!this.lightProbe || !frame) return;
  
    try {
      // Only update every N frames for performance
      if (this.frameCount % this.lightUpdateInterval !== 0) return;
      
      const lightEstimate = frame.getLightEstimate(this.lightProbe);
      if (lightEstimate) {
        this.lightEstimate = lightEstimate;
        this.updateSceneLighting();
      }
    } catch (error) {
      this.error('Error updating light estimation:', error);
    }
  }

  /**
   * Update scene lighting based on light estimation
   * @private
   */
  updateSceneLighting() {
    if (!this.lightEstimate || !this.scene) return;
  
    try {
      // Update ambient light
      const ambientIntensity = this.lightEstimate.ambientIntensity;
      const ambientLight = this.scene.querySelector('[light="type: ambient"]');
      if (ambientLight) {
        ambientLight.setAttribute('light', 'intensity', ambientIntensity * this.defaultLightIntensity);
      } else {
        const newAmbientLight = document.createElement('a-entity');
        newAmbientLight.setAttribute('light', {
          type: 'ambient',
          intensity: ambientIntensity * this.defaultLightIntensity
        });
        this.scene.appendChild(newAmbientLight);
      }
  
      // Update directional light
      const directionalIntensity = Math.max(...this.lightEstimate.primaryLightIntensity);
      const directionalLight = this.scene.querySelector('[light="type: directional"]');
      if (directionalLight) {
        directionalLight.setAttribute('light', 'intensity', directionalIntensity * this.defaultLightIntensity);
        if (this.lightEstimate.primaryLightDirection) {
          directionalLight.setAttribute('position', this.arrayToVec3(this.lightEstimate.primaryLightDirection));
        }
      } else {
        const newDirectionalLight = document.createElement('a-entity');
        newDirectionalLight.setAttribute('light', {
          type: 'directional',
          intensity: directionalIntensity * this.defaultLightIntensity
        });
        if (this.lightEstimate.primaryLightDirection) {
          newDirectionalLight.setAttribute('position', this.arrayToVec3(this.lightEstimate.primaryLightDirection));
        }
        this.scene.appendChild(newDirectionalLight);
      }
  
      // Update environment map intensity for all entities with standard material
      const entities = this.scene.querySelectorAll('[material="shader: standard"]');
      entities.forEach(entity => {
        entity.setAttribute('material', 'envMapIntensity', this.lightEstimate.ambientIntensity * this.defaultEnvironmentIntensity);
      });
    } catch (error) {
      this.error('Error updating scene lighting:', error);
    }
  }

  /**
   * Set up depth sensing
   * @private
   * @returns {Promise<boolean>} - Whether setup was successful
   */
  async setupDepthSensing() {
    if (!this.supportedFeatures.has('depth-sensing')) {
      this.log('Depth sensing is not supported on this device.');
      return false;
    }
  
    this.log('Setting up depth-sensing');
  
    try {
      // Check for depth-sensing support in different ways for browser compatibility
      let depthSensingSupported = false;
      
      // Try the standard WebXR API first
      if (this.xrSession.depthUsage !== undefined && this.xrSession.depthDataFormat !== undefined) {
        depthSensingSupported = true;
      }
      
      // Try feature descriptor approach
      if (!depthSensingSupported && this.xrSession.enabledFeatures) {
        depthSensingSupported = this.xrSession.enabledFeatures.includes('depth-sensing');
      }
      
      if (!depthSensingSupported) {
        this.warn('Depth sensing appears to be unsupported despite initial feature check');
        return false;
      }
      
      // Set up depth sensing
      await this.xrSession.updateRenderState({
        depthSensing: {
          usagePreference: ['cpu-optimized'],
          dataFormatPreference: ['luminance-alpha']
        }
      });
      
      this.log('Depth sensing enabled');
      return true;
    } catch (error) {
      this.error('Error setting up depth sensing:', error);
      return false;
    }
  }

  /**
   * Get depth data at a specific screen coordinate
   * @param {number} x - Normalized X coordinate (0-1)
   * @param {number} y - Normalized Y coordinate (0-1)
   * @returns {number|null} - Depth in meters or null if not available
   */
  getDepthAtScreenPoint(x, y) {
    if (!this.depthSensing) {
      return null;
    }
  
    try {
      const depthData = this.depthSensing.getData();
      if (!depthData || !this.depthSensing.width || !this.depthSensing.height) {
        return null;
      }
      
      const width = this.depthSensing.width;
      const height = this.depthSensing.height;
  
      // Convert normalized screen coordinates to depth texture coordinates
      const depthX = Math.floor(x * width);
      const depthY = Math.floor(y * height);
  
      const index = depthY * width + depthX;
      if (index < 0 || index >= depthData.length) {
        return null;
      }
      
      const rawDepth = depthData[index];
  
      // Convert raw depth to meters
      const depthInMeters = rawDepth * this.depthSensing.rawValueToMeters;
  
      return depthInMeters;
    } catch (error) {
      this.error('Error getting depth at screen point:', error);
      return null;
    }
  }

  /**
   * Set up image tracking
   * @param {Array<Object>} imageTargets - Array of image targets to track
   * @param {string} imageTargets[].src - URL of the image to track
   * @param {number} imageTargets[].widthInMeters - Physical width of the image in meters
   * @returns {Promise<boolean>} - Whether setup was successful
   */
  async setupImageTracking(imageTargets) {
    if (!this.supportedFeatures.has('image-tracking')) {
      this.log('Image tracking is not supported on this device.');
      return false;
    }
  
    this.log('Setting up image-tracking');
  
    try {
      // Validate image targets
      if (!Array.isArray(imageTargets) || imageTargets.length === 0) {
        this.error('Invalid image targets: must be a non-empty array');
        return false;
      }
      
      // Check for required properties on all targets
      const validTargets = imageTargets.filter(target => 
        target && typeof target === 'object' && 
        typeof target.src === 'string' && 
        typeof target.widthInMeters === 'number' && 
        target.widthInMeters > 0
      );
      
      if (validTargets.length === 0) {
        this.error('No valid image targets provided');
        return false;
      }
      
      if (validTargets.length < imageTargets.length) {
        this.warn(`Only ${validTargets.length} of ${imageTargets.length} image targets are valid`);
      }
      
      // Get tracking scores if the API supports it
      if (this.xrSession.getTrackedImageScores) {
        try {
          const imageTrackingResults = await this.xrSession.getTrackedImageScores();
          
          for (const [index, score] of imageTrackingResults.entries()) {
            this.log(`Image ${index} tracking score: ${score}`);
          }
        } catch (err) {
          this.warn('Error getting image tracking scores:', err);
        }
      }
  
      // Register images for tracking
      await this.xrSession.trackImages(validTargets);
  
      // Set up event listener for tracking results
      this.xrSession.addEventListener('imagetrackingesults', this.onImageTrackingResults);
  
      this.log('Image tracking setup complete with', validTargets.length, 'images');
      return true;
    } catch (error) {
      this.error('Error setting up image tracking:', error);
      return false;
    }
  }

  /**
   * Handle image tracking results
   * @private
   * @param {Event} event - The image tracking results event
   */
  onImageTrackingResults(event) {
    try {
      const results = event.results;
      
      for (const result of results) {
        const imageIndex = result.index;
        
        if (result.trackingState === 'tracked') {
          const pose = result.pose;
          this.imageTrackingResults.set(imageIndex, pose);
          this.updateTrackedImageEntity(imageIndex, pose);
          
          // Emit an event for the app to use
          this.scene.emit('image-tracked', { 
            index: imageIndex, 
            pose: pose,
            state: 'tracked'
          });
        } else if (result.trackingState === 'emulated') {
          this.log(`Image ${imageIndex} tracking emulated`);
          // Emit an event for the app to use
          this.scene.emit('image-tracked', { 
            index: imageIndex, 
            state: 'emulated'
          });
        } else {
          this.imageTrackingResults.delete(imageIndex);
          this.removeTrackedImageEntity(imageIndex);
          
          // Emit an event for the app to use
          this.scene.emit('image-tracked', { 
            index: imageIndex, 
            state: 'lost'
          });
        }
      }
    } catch (error) {
      this.error('Error handling image tracking results:', error);
    }
  }

  /**
   * Update the entity representing a tracked image
   * @private
   * @param {number} imageIndex - Index of the tracked image
   * @param {XRPose} pose - Pose of the tracked image
   */
  updateTrackedImageEntity(imageIndex, pose) {
    try {
      let entity = this.trackedImages.get(imageIndex);
      
      if (!entity) {
        entity = document.createElement('a-entity');
        entity.setAttribute('id', `tracked-image-${imageIndex}`);
        this.scene.appendChild(entity);
        this.trackedImages.set(imageIndex, entity);
      }
  
      const position = pose.transform.position;
      const orientation = pose.transform.orientation;
      
      entity.setAttribute('position', `${position.x} ${position.y} ${position.z}`);
      entity.setAttribute('rotation', this.getRotationFromQuaternion(orientation));
    } catch (error) {
      this.error('Error updating tracked image entity:', error);
    }
  }

  /**
   * Remove the entity representing a tracked image
   * @private
   * @param {number} imageIndex - Index of the tracked image
   */
  removeTrackedImageEntity(imageIndex) {
    try {
      const entity = this.trackedImages.get(imageIndex);
      if (entity) {
        this.scene.removeChild(entity);
        this.trackedImages.delete(imageIndex);
      }
    } catch (error) {
      this.error('Error removing tracked image entity:', error);
    }
  }

  /**
   * Set up anchor support
   * @private
   * @returns {Promise<boolean>} - Whether setup was successful
   */
  async setupAnchors() {
    if (!this.supportedFeatures.has('anchors')) {
      this.log('Anchors are not supported on this device.');
      return false;
    }
  
    this.log('Setting up anchor support');
  
    try {
      // Additional check for anchor support
      const anchorSupport = await this.xrSession.supportsSession('immersive-ar', { requiredFeatures: ['anchors'] });
      
      if (anchorSupport) {
        this.log('Anchor support is available');
        this.xrSession.addEventListener('anchorchange', this.onAnchorUpdate);
        return true;
      } else {
        this.log('Anchor support is not available');
        return false;
      }
    } catch (error) {
      this.error('Error setting up anchor support:', error);
      return false;
    }
  }

  /**
   * Create an anchor at a specific pose
   * @param {XRPose} pose - The pose to create the anchor at
   * @returns {Promise<string|null>} - The anchor ID or null if creation failed
   */
  async createAnchor(pose) {
    if (!this.xrSession || !this.xrRefSpace) {
      this.error('XR session or reference space not available');
      return null;
    }
  
    try {
      const anchor = await this.xrSession.createAnchor(pose, this.xrRefSpace);
      const anchorId = `anchor-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      this.anchors.set(anchorId, { anchor, entities: [] });
      this.log(`Anchor created with ID: ${anchorId}`);
      
      // Emit event for anchor creation
      this.scene.emit('anchor-created', { 
        id: anchorId, 
        anchor: anchor
      });
      
      return anchorId;
    } catch (error) {
      this.error('Error creating anchor:', error);
      return null;
    }
  }

  /**
   * Create an anchor from a hit test result
   * @param {XRHitTestResult} hitTestResult - The hit test result to create an anchor from
   * @returns {Promise<string|null>} - The anchor ID or null if creation failed
   */
  async createAnchorFromHitTest(hitTestResult) {
    try {
      if (!hitTestResult) {
        this.error('Invalid hit test result');
        return null;
      }
      
      if (!hitTestResult.createAnchor) {
        this.error('This hit test result does not support anchor creation');
        return null;
      }
      
      const anchor = await hitTestResult.createAnchor();
      const anchorId = `anchor-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      this.anchors.set(anchorId, { anchor, entities: [] });
      this.log(`Anchor created from hit test with ID: ${anchorId}`);
      
      // Emit event for anchor creation
      this.scene.emit('anchor-created', { 
        id: anchorId, 
        anchor: anchor,
        fromHitTest: true
      });
      
      return anchorId;
    } catch (error) {
      this.error('Error creating anchor from hit test:', error);
      return null;
    }
  }

  /**
   * Attach an A-Frame entity to an anchor
   * @param {string|HTMLElement} entityId - The entity ID or element to attach
   * @param {string} anchorId - The anchor ID to attach to
   * @returns {boolean} - Whether the attachment was successful
   */
  attachEntityToAnchor(entityId, anchorId) {
    try {
      const entity = typeof entityId === 'string' 
        ? document.getElementById(entityId) 
        : entityId instanceof HTMLElement 
          ? entityId 
          : null;
          
      const anchorData = this.anchors.get(anchorId);
    
      if (!entity || !anchorData) {
        this.error('Entity or anchor not found');
        return false;
      }
    
      anchorData.entities.push(entity);
      
      // Emit event for entity attachment
      this.scene.emit('entity-anchored', { 
        entity: entity, 
        anchorId: anchorId
      });
      
      return true;
    } catch (error) {
      this.error('Error attaching entity to anchor:', error);
      return false;
    }
  }

  /**
   * Remove an anchor and unanchor attached entities
   * @param {string} anchorId - ID of the anchor to remove
   * @returns {boolean} - Whether the anchor was successfully removed
   */
  removeAnchor(anchorId) {
    try {
      const anchorData = this.anchors.get(anchorId);
      if (anchorData) {
        // Try to delete the anchor if possible
        try {
          if (anchorData.anchor && typeof anchorData.anchor.delete === 'function') {
            anchorData.anchor.delete();
          }
        } catch (err) {
          this.warn(`Could not delete anchor ${anchorId}:`, err);
        }
        
        this.anchors.delete(anchorId);
        
        // Emit event for anchor removal
        this.scene.emit('anchor-removed', { 
          id: anchorId
        });
        
        this.log(`Anchor ${anchorId} removed`);
        return true;
      } else {
        this.error(`Anchor ${anchorId} not found`);
        return false;
      }
    } catch (error) {
      this.error('Error removing anchor:', error);
      return false;
    }
  }

  /**
   * Handle anchor update events
   * @private
   * @param {Event} event - The anchor update event
   */
  onAnchorUpdate(event) {
    try {
      for (const { anchor, anchorSpace } of event.anchors) {
        const anchorId = this.getAnchorIdByAnchorObject(anchor);
        if (anchorId) {
          const anchorPose = anchorSpace.getPose(this.xrRefSpace);
          if (anchorPose) {
            this.updateAnchoredEntities(anchorId, anchorPose);
            
            // Emit event for anchor update
            this.scene.emit('anchor-updated', { 
              id: anchorId, 
              pose: anchorPose
            });
          }
        }
      }
    } catch (error) {
      this.error('Error handling anchor update:', error);
    }
  }

  /**
   * Get anchor ID by anchor object
   * @private
   * @param {XRAnchor} anchorObject - The anchor object
   * @returns {string|null} - The anchor ID or null if not found
   */
  getAnchorIdByAnchorObject(anchorObject) {
    for (const [id, data] of this.anchors.entries()) {
      if (data.anchor === anchorObject) {
        return id;
      }
    }
    return null;
  }

  /**
   * Update all entities attached to an anchor
   * @private
   * @param {string} anchorId - ID of the anchor
   * @param {XRPose} anchorPose - Pose of the anchor
   */
  updateAnchoredEntities(anchorId, anchorPose) {
    try {
      const anchorData = this.anchors.get(anchorId);
      if (anchorData) {
        anchorData.entities.forEach(entity => {
          entity.setAttribute('position', this.vectorToString(anchorPose.transform.position));
          entity.setAttribute('rotation', this.getRotationFromQuaternion(anchorPose.transform.orientation));
        });
      }
    } catch (error) {
      this.error('Error updating anchored entities:', error);
    }
  }

  /**
   * Set up mesh detection
   * @private
   * @returns {Promise<boolean>} - Whether setup was successful
   */
  async setupMeshDetection() {
    if (!this.supportedFeatures.has('mesh-detection')) {
      this.log('Mesh detection is not supported on this device.');
      return false;
    }
  
    this.log('Setting up mesh-detection');
  
    try {
      this.xrSession.addEventListener('meshesdetected', (event) => {
        for (const mesh of event.meshes) {
          if (mesh.meshType === 'mesh') {
            this.updateMesh(mesh);
            
            // Emit event for mesh detection
            this.scene.emit('mesh-detected', { 
              id: mesh.meshId, 
              mesh: mesh
            });
          }
        }
      });
      
      return true;
    } catch (error) {
      this.error('Error setting up mesh detection:', error);
      return false;
    }
  }

  /**
   * Update detected mesh
   * @private
   * @param {XRMesh} mesh - The detected mesh
   */
  updateMesh(mesh) {
    try {
      if (this.meshes.has(mesh.meshId)) {
        // Update existing mesh
        const existingMesh = this.meshes.get(mesh.meshId);
        existingMesh.setAttribute('position', this.arrayToVec3(mesh.position));
        existingMesh.setAttribute('rotation', this.arrayToVec3(mesh.orientation));
        existingMesh.setAttribute('scale', this.arrayToVec3(mesh.scale));
      } else {
        // Create new mesh
        const newMesh = this.createMeshEntity(mesh);
        this.meshes.set(mesh.meshId, newMesh);
        this.scene.appendChild(newMesh);
      }
    } catch (error) {
      this.error('Error updating mesh:', error);
    }
  }

  /**
   * Create an A-Frame entity for a detected mesh
   * @private
   * @param {XRMesh} mesh - The detected mesh
   * @returns {HTMLElement} - The created A-Frame entity
   */
  createMeshEntity(mesh) {
    try {
      const entity = document.createElement('a-entity');
      entity.setAttribute('id', `mesh-${mesh.meshId}`);
      entity.setAttribute('geometry', {
        primitive: 'mesh',
        vertices: mesh.vertices,
        indices: mesh.indices
      });
      entity.setAttribute('material', {
        color: '#7e7e7e',
        opacity: 0.5,
        transparent: true,
        wireframe: true
      });
      entity.setAttribute('position', this.arrayToVec3(mesh.position));
      entity.setAttribute('rotation', this.arrayToVec3(mesh.orientation));
      entity.setAttribute('scale', this.arrayToVec3(mesh.scale));
      return entity;
    } catch (error) {
      this.error('Error creating mesh entity:', error);
      // Return a basic entity as fallback
      const fallbackEntity = document.createElement('a-entity');
      fallbackEntity.setAttribute('id', `mesh-${mesh.meshId}-fallback`);
      return fallbackEntity;
    }
  }

  /**
   * Set up plane detection
   * @private
   * @returns {Promise<boolean>} - Whether setup was successful
   */
  async setupPlaneDetection() {
    if (!this.supportedFeatures.has('plane-detection')) {
      this.log('Plane detection is not supported on this device.');
      return false;
    }
  
    this.log('Setting up plane-detection');
  
    try {
      this.xrSession.addEventListener('planedetected', (event) => {
        this.updatePlane(event.plane);
        
        // Emit event for plane detection
        this.scene.emit('plane-detected', { 
          id: event.plane.planeId, 
          plane: event.plane
        });
      });
  
      this.xrSession.addEventListener('planeremoved', (event) => {
        this.removePlane(event.plane);
        
        // Emit event for plane removal
        this.scene.emit('plane-removed', { 
          id: event.plane.planeId
        });
      });
      
      return true;
    } catch (error) {
      this.error('Error setting up plane detection:', error);
      return false;
    }
  }

  /**
   * Update detected plane
   * @private
   * @param {XRPlane} plane - The detected plane
   */
  updatePlane(plane) {
    try {
      if (this.planes.has(plane.planeId)) {
        // Update existing plane
        const existingPlane = this.planes.get(plane.planeId);
        this.updatePlaneEntity(existingPlane, plane);
      } else {
        // Create new plane
        const newPlane = this.createPlaneEntity(plane);
        this.planes.set(plane.planeId, newPlane);
        this.scene.appendChild(newPlane);
      }
    } catch (error) {
      this.error('Error updating plane:', error);
    }
  }

  /**
   * Remove detected plane
   * @private
   * @param {XRPlane} plane - The plane to remove
   */
  removePlane(plane) {
    try {
      if (this.planes.has(plane.planeId)) {
        const planeEntity = this.planes.get(plane.planeId);
        this.scene.removeChild(planeEntity);
        this.planes.delete(plane.planeId);
      }
    } catch (error) {
      this.error('Error removing plane:', error);
    }
  }

  /**
   * Create an A-Frame entity for a detected plane
   * @private
   * @param {XRPlane} plane - The detected plane
   * @returns {HTMLElement} - The created A-Frame entity
   */
  createPlaneEntity(plane) {
    try {
      const entity = document.createElement('a-entity');
      entity.setAttribute('id', `plane-${plane.planeId}`);
      this.updatePlaneEntity(entity, plane);
      return entity;
    } catch (error) {
      this.error('Error creating plane entity:', error);
      // Return a basic entity as fallback
      const fallbackEntity = document.createElement('a-entity');
      fallbackEntity.setAttribute('id', `plane-${plane.planeId}-fallback`);
      return fallbackEntity;
    }
  }

  /**
   * Update an A-Frame entity for a detected plane
   * @private
   * @param {HTMLElement} entity - The A-Frame entity to update
   * @param {XRPlane} plane - The detected plane
   */
  updatePlaneEntity(entity, plane) {
    try {
      const pose = plane.pose;
      const dimensions = plane.dimensions;
  
      entity.setAttribute('position', this.arrayToVec3(pose.transform.position));
      entity.setAttribute('rotation', this.getRotationFromMatrix(pose.transform.matrix));
      entity.setAttribute('geometry', {
        primitive: 'plane',
        width: dimensions.x,
        height: dimensions.y
      });
      entity.setAttribute('material', {
        color: this.getPlaneColor(plane.orientation),
        transparent: true,
        opacity: 0.5
      });
    } catch (error) {
      this.error('Error updating plane entity:', error);
    }
  }

  /**
   * Get color for plane based on orientation
   * @private
   * @param {string} orientation - Orientation of the plane
   * @returns {string} - Color for the plane
   */
  getPlaneColor(orientation) {
    switch (orientation) {
      case 'horizontal':
        return '#7CFC00';  // LawnGreen
      case 'vertical':
        return '#1E90FF';  // DodgerBlue
      default:
        return '#FFD700';  // Gold
    }
  }

  /**
   * Start an XR session
   * @param {string} mode - The XR session mode ('immersive-ar', 'immersive-vr', etc.)
   * @returns {Promise<boolean>} - Whether the session started successfully
   */
  async startXR(mode) {
    if (!navigator.xr) {
      this.error('WebXR not supported in this browser');
      return false;
    }
  
    if (this.xrSession) {
      this.warn('XR session already running. Please end the current session before starting a new one.');
      return false;
    }
  
    try {
      const supported = await this.isSessionModeSupported(mode);
      if (!supported) {
        this.error(`${mode} mode not supported by this device`);
        return false;
      }
  
      // Check feature support
      await this.checkFeatureSupport(mode);
  
      // Prepare session features
      const requiredFeatures = ['local-floor'];
      const optionalFeatures = [
        'hit-test', 
        'light-estimation', 
        'depth-sensing', 
        'dom-overlay', 
        'image-tracking', 
        'anchors', 
        'plane-detection', 
        'mesh-detection'
      ];
      
      // Only include supported features
      const filteredOptionalFeatures = optionalFeatures.filter(feature => 
        this.supportedFeatures.has(feature)
      );
      
      // Create session init object
      const sessionInit = {
        requiredFeatures: requiredFeatures,
        optionalFeatures: filteredOptionalFeatures
      };
  
      // Add DOM overlay if available and in AR mode
      if (this.domOverlayElement && mode === 'immersive-ar') {
        sessionInit.domOverlay = { root: this.domOverlayElement };
        this.log('Using DOM overlay:', this.domOverlayElement);
      }
  
      // Request the XR session
      this.xrSession = await navigator.xr.requestSession(mode, sessionInit);
  
      // Set mode state
      this.currentMode = mode;
      this.isAR = mode === 'immersive-ar';
      this.isVR = mode === 'immersive-vr';
  
      // Integrate with A-Frame if available
      if (this.scene && typeof this.scene.enterVR === 'function') {
        this.scene.enterVR();
      }
  
      // Set up session features
      const setupTasks = [
        this.setupLocalFloor()
      ];
      
      if (this.isAR) {
        setupTasks.push(
          this.setupHitTest(),
          this.setupLightEstimation(),
          this.setupDepthSensing(),
          this.setupAnchors(),
          this.setupPlaneDetection(),
          this.setupMeshDetection()
        );
      }
      
      // Execute all setup tasks in parallel
      await Promise.allSettled(setupTasks);
  
      // Attach HandDetection to WebXR session if available
      if (this.handDetection) {
        try {
          this.handDetection.attachToWebXRSession(this.xrSession);
          this.log('Hand detection attached to XR session');
        } catch (handError) {
          this.warn('Failed to attach hand detection to XR session:', handError);
        }
      }
  
      // Set up session end handler
      this.xrSession.addEventListener('end', () => {
        this.log('XR session ended');
        this.cleanupXRSession();
      });
  
      // Request reference space and start animation loop
      this.xrRefSpace = await this.xrSession.requestReferenceSpace('local');
      this.xrSession.requestAnimationFrame(this.onXRFrame);
      
      // Emit event for session start
      this.scene.emit('xr-session-started', { 
        mode: mode,
        session: this.xrSession
      });
      
      this.log(`${mode} session started successfully`);
      return true;
  
    } catch (error) {
      this.error(`Error starting ${mode} session:`, error);
      this.cleanupXRSession();
      return false;
    }
  }

  /**
   * Switch between AR and VR modes
   * @returns {Promise<boolean>} - Whether the mode switch was successful
   */
  async switchMode() {
    if (!this.xrSession) {
      this.warn('No active XR session. Please start a session first.');
      return false;
    }
  
    try {
      const newMode = this.currentMode === 'immersive-ar' ? 'immersive-vr' : 'immersive-ar';
      
      await this.endXR();
      const success = await this.startXR(newMode);
      
      if (success) {
        this.log(`Successfully switched to ${newMode} mode.`);
        return true;
      } else {
        this.error(`Failed to switch to ${newMode} mode.`);
        return false;
      }
    } catch (error) {
      this.error('Error switching XR mode:', error);
      return false;
    }
  }

  /**
   * End the current XR session
   * @returns {Promise<boolean>} - Whether the session ended successfully
   */
  async endXR() {
    if (!this.xrSession) {
      this.log('No active XR session to end.');
      return true;
    }
  
    try {
      await this.xrSession.end();
      // Note: cleanup will be done in the 'end' event handler
      return true;
    } catch (error) {
      this.error('Error ending XR session:', error);
      
      // Force cleanup if end() failed
      this.cleanupXRSession();
      return false;
    }
  }

  /**
   * Clean up after an XR session ends
   * @private
   */
  cleanupXRSession() {
    this.isAR = false;
    this.isVR = false;
    this.currentMode = null;
    
    // Clear feature instances
    this.hitTestSource = null;
    this.lightProbe = null;
    this.lightEstimate = null;
    this.depthSensing = null;
    this.xrRefSpace = null;
    this.viewerSpace = null;
    this.localFloor = null;
    
    // Clear session reference
    this.xrSession = null;
    
    // Note: we don't clear maps (planes, anchors, etc.) here
    // as they might be useful for the next session
    
    // Emit event for session end
    if (this.scene) {
      this.scene.emit('xr-session-ended');
    }
    
    this.log('XR session cleanup complete');
  }

  /**
   * Handle XR animation frame
   * @private
   * @param {number} time - Current time in milliseconds
   * @param {XRFrame} frame - The current XR frame
   */
  onXRFrame(time, frame) {
    try {
      // Increment frame counter
      this.frameCount++;
      
      // Store frame reference
      this.xrFrame = frame;
      
      // Track performance
      this.trackPerformance(time);
      
      // Process AR-specific features if in AR mode
      if (this.isAR) {
        // Light estimation
        if (this.lightProbe) {
          this.updateLightEstimation(frame);
        }
        
        // Depth sensing
        if (this.supportedFeatures.has('depth-sensing')) {
          try {
            const viewerPose = frame.getViewerPose(this.xrRefSpace);
            if (viewerPose && viewerPose.views.length > 0) {
              this.depthSensing = frame.getDepthInformation(viewerPose.views[0]);
            }
          } catch (depthError) {
            // Depth information might not be available every frame
            if (this.frameCount % 90 === 0) { // Log only once every 90 frames
              this.warn('Error getting depth information:', depthError);
            }
          }
        }
        
        // Hit testing
        if (this.hitTestSource) {
          this.performHitTest(frame);
        }
        
        // Update floor level
        this.updateFloorLevel();
      }
      
      // Call user-defined frame callback if provided
      if (this.frameCallback && typeof this.frameCallback === 'function') {
        try {
          this.frameCallback(time, frame, this);
        } catch (callbackError) {
          this.error('Error in user frame callback:', callbackError);
        }
      }
      
      // Request next frame if session is still active
      if (this.xrSession) {
        this.xrSession.requestAnimationFrame(this.onXRFrame);
      }
    } catch (error) {
      this.error('Error in XR frame:', error);
      
      // Continue the frame loop even if there was an error
      if (this.xrSession) {
        this.xrSession.requestAnimationFrame(this.onXRFrame);
      }
    }
  }

  /**
   * Track performance metrics
   * @private
   * @param {number} time - Current time in milliseconds
   */
  trackPerformance(time) {
    try {
      if (this.performanceMetrics.lastFrameTimestamp === 0) {
        this.performanceMetrics.lastFrameTimestamp = time;
        return;
      }
      
      const frameDelta = time - this.performanceMetrics.lastFrameTimestamp;
      this.performanceMetrics.lastFrameTimestamp = time;
      
      // Update running average
      const frameTime = frameDelta;
      const frameCount = this.performanceMetrics.frameCount;
      
      if (frameCount === 0) {
        this.performanceMetrics.avgFrameTime = frameTime;
      } else {
        // Weighted moving average (newer frames have more weight)
        this.performanceMetrics.avgFrameTime = 
          this.performanceMetrics.avgFrameTime * 0.95 + frameTime * 0.05;
      }
      
      this.performanceMetrics.frameCount++;
      
      // Log performance every 100 frames
      if (this.performanceMetrics.frameCount % 100 === 0 && this.debugMode) {
        const fps = 1000 / this.performanceMetrics.avgFrameTime;
        this.log(`Performance: ${fps.toFixed(1)} FPS (${this.performanceMetrics.avgFrameTime.toFixed(2)}ms per frame)`);
      }
    } catch (error) {
      // Quietly ignore performance tracking errors
    }
  }

  /**
   * Set a custom frame callback
   * @param {Function} callback - Function to call on each XR frame
   */
  setFrameCallback(callback) {
    if (typeof callback === 'function') {
      this.frameCallback = callback;
    } else {
      this.warn('Invalid frame callback. Please provide a function.');
    }
  }

  /**
   * Update floor level
   * @private
   */
  updateFloorLevel() {
    if (!this.localFloor) return;
  
    try {
      const floorLevel = this.localFloor.getOffsetReferenceSpace(new XRRigidTransform({y: 0}));
      this.scene.emit('ar-floor-level-updated', { floorLevel });
    } catch (error) {
      // Floor level updates can fail silently
    }
  }

  /**
   * Convert an array to a vec3 string
   * @private
   * @param {Array<number>} array - Array of 3 numbers
   * @returns {string} - Space-separated string for vec3
   */
  arrayToVec3(array) {
    if (!Array.isArray(array) || array.length < 3) {
      return '0 0 0';
    }
    return array.slice(0, 3).join(' ');
  }

  /**
   * Convert a vector to a string
   * @private
   * @param {Object} vector - Vector with x, y, z properties
   * @returns {string} - Space-separated string for vec3
   */
  vectorToString(vector) {
    if (!vector || typeof vector !== 'object') {
      return '0 0 0';
    }
    return `${vector.x.toFixed(3)} ${vector.y.toFixed(3)} ${vector.z.toFixed(3)}`;
  }

  /**
   * Get rotation from a transformation matrix
   * @private
   * @param {Float32Array} matrix - 4x4 transformation matrix
   * @returns {string} - Space-separated string for rotation
   */
  getRotationFromMatrix(matrix) {
    try {
      // Check if THREE.js is available
      if (typeof THREE === 'undefined' || !THREE.Matrix4 || !THREE.Euler) {
        return '0 0 0';
      }
      
      const rotationMatrix = new THREE.Matrix4().fromArray(matrix);
      const euler = new THREE.Euler().setFromRotationMatrix(rotationMatrix);
      return `${THREE.MathUtils.radToDeg(euler.x)} ${THREE.MathUtils.radToDeg(euler.y)} ${THREE.MathUtils.radToDeg(euler.z)}`;
    } catch (error) {
      this.warn('Error extracting rotation from matrix:', error);
      return '0 0 0';
    }
  }

  /**
   * Get rotation from a quaternion
   * @private
   * @param {Object} quaternion - Quaternion with x, y, z, w properties
   * @returns {string} - Space-separated string for rotation
   */
  getRotationFromQuaternion(quaternion) {
    try {
      // Check if THREE.js is available
      if (typeof THREE === 'undefined' || !THREE.Quaternion || !THREE.Euler) {
        // Fallback to identity rotation
        return '0 0 0';
      }
      
      const euler = new THREE.Euler().setFromQuaternion(
        new THREE.Quaternion(quaternion.x, quaternion.y, quaternion.z, quaternion.w)
      );
      return `${THREE.MathUtils.radToDeg(euler.x)} ${THREE.MathUtils.radToDeg(euler.y)} ${THREE.MathUtils.radToDeg(euler.z)}`;
    } catch (error) {
      this.warn('Error extracting rotation from quaternion:', error);
      return '0 0 0';
    }
  }
  
  /**
   * Get the current performance metrics
   * @returns {Object} - Object containing performance metrics
   */
  getPerformanceMetrics() {
    const fps = 1000 / this.performanceMetrics.avgFrameTime;
    return {
      fps: fps,
      frameTime: this.performanceMetrics.avgFrameTime,
      frameCount: this.performanceMetrics.frameCount
    };
  }
  
  /**
   * Get all detected planes
   * @returns {Array<Object>} - Array of plane data
   */
  getDetectedPlanes() {
    const planeData = [];
    for (const [id, entity] of this.planes.entries()) {
      planeData.push({
        id: id,
        element: entity,
        position: entity.getAttribute('position'),
        rotation: entity.getAttribute('rotation')
      });
    }
    return planeData;
  }
  
  /**
   * Get all tracked images
   * @returns {Array<Object>} - Array of tracked image data
   */
  getTrackedImages() {
    const imageData = [];
    for (const [index, entity] of this.trackedImages.entries()) {
      imageData.push({
        index: index,
        element: entity,
        position: entity.getAttribute('position'),
        rotation: entity.getAttribute('rotation')
      });
    }
    return imageData;
  }
  
  /**
   * Get all created anchors
   * @returns {Array<Object>} - Array of anchor data
   */
  getAnchors() {
    const anchorData = [];
    for (const [id, data] of this.anchors.entries()) {
      anchorData.push({
        id: id,
        entities: data.entities.map(entity => entity.id || 'unnamed-entity')
      });
    }
    return anchorData;
  }
  
  /**
   * Get the last hit test pose
   * @returns {XRPose|null} - The last hit test pose or null if none
   */
  getLastHitTestPose() {
    return this.lastHitTestPose;
  }
}

export default MixedReality;